import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { AuthContext } from "./AuthContext";
import api from "../services/api";
import { trackAddToCart } from "../services/analytics";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const { token } = useContext(AuthContext);

  const [cartItems, setCartItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);

  // ─── Let api.js interceptor handle ALL auth headers automatically ─────────
  // Do NOT pass manual headers — the interceptor in api.js already reads
  // localStorage for authToken and isGuest and sets the correct header.
  // Passing { headers } manually was causing conflicts.

  const fetchCartItems = useCallback(async () => {
    console.log("fetchCartItems called");
    setIsLoading(true);
    try {
      const res = await api.get("/cart"); // ← no manual headers
      console.log("Cart fetch result →", res.data);
      const items = res.data.items || [];
      setCartItems(items);
      return items; // lets addToCart read the just-refreshed list without a stale closure
    } catch (error) {
      console.error("Fetch cart error:", error);
      setCartItems([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []); // ← no dependency on getAuthHeaders

  const addToCart = async (productId, quantity = 1) => {
    try {
      const payload = { product_id: productId, quantity };
      await api.post("/cart/add", payload); // ← no manual headers
      const items = await fetchCartItems();
      // The GA4 event needs the product's name/price, which we don't have
      // here (only productId) — pull it from the freshly-refetched, populated
      // cart rather than requiring every caller to pass the full product.
      const added = items.find((it) => (it.product_id?._id || it.product_id) === productId);
      if (added) trackAddToCart(added.product_id, quantity);
    } catch (error) {
      console.error("Error adding to cart:", error);
      throw error;
    }
  };

  const removeFromCart = async (productId) => {
    try {
      await api.delete(`/cart/remove/${productId}`); // ← no manual headers
      setCartItems((prev) =>
        prev.filter(
          (item) => (item.product_id._id || item.product_id) !== productId
        )
      );
    } catch (error) {
      console.error("Error removing from cart:", error);
      throw error;
    }
  };

  const updateCartItemQuantity = async (productId, quantity) => {
    try {
      if (quantity <= 0) {
        await removeFromCart(productId);
        return;
      }
      const payload = { product_id: productId, quantity };
      await api.put("/cart/update", payload); // ← no manual headers
      setCartItems((prev) =>
        prev.map((item) =>
          (item.product_id._id || item.product_id) === productId
            ? { ...item, quantity }
            : item
        )
      );
    } catch (error) {
      console.error("Error updating cart item:", error);
      throw error;
    }
  };

  const clearCart = async () => {
    try {
      console.log("clearCart called");
      await api.delete("/cart/clear"); // ← no manual headers
      setCartItems([]);
      console.log("Cart cleared successfully");
    } catch (error) {
      console.error("Error clearing cart:", error);
      setCartItems([]);
    }
  };

  const getCartItemCount = useCallback(() => {
    return cartItems.reduce((total, item) => total + (item.quantity || 0), 0);
  }, [cartItems]);

  const getCartTotal = useCallback(() => {
    return cartItems.reduce((total, item) => {
      const product = item.product_id;
      const price = product?.is_on_sale ? product?.sale_price : product?.sell_price;
      return total + (price || 0) * (item.quantity || 0);
    }, 0);
  }, [cartItems]);

  // ─── Wait for token to be set before fetching cart ───────────────────────
  useEffect(() => {
    if (token !== undefined && token !== null) {
      fetchCartItems().then(() => setAuthReady(true));
    } else if (token === null) {
      setIsLoading(false);
      setAuthReady(true);
    }
  }, [token, fetchCartItems]);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        fetchCartItems,
        isLoading,
        authReady,
        addToCart,
        removeFromCart,
        updateCartItemQuantity,
        clearCart,
        getCartItemCount,
        getCartTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};